/**
 * Shared filename helper for all admin exports (PDF, CSV, ICS).
 *
 * Produces consistent, human-readable filenames:
 *   slug(event.name) + "-" + kind + ["-" + audienceSlug] + "-" + YYYY-MM-DD + "." + ext
 *
 * Examples:
 *   flat-rock-winter-summit-2026-agenda-2026-04-17.pdf
 *   flat-rock-winter-summit-2026-agenda-internal-2026-04-17.csv
 *   flat-rock-winter-summit-2026-roster-2026-04-17.csv
 */

/**
 * Slugify a string: lowercase, replace non-alphanumeric runs with hyphens,
 * trim leading/trailing hyphens, collapse consecutive hyphens.
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

export interface ExportFilenameOptions {
  /** Event name (will be slugified). */
  eventName: string;
  /** Export kind — e.g. "agenda", "roster", "assignments". */
  kind: string;
  /** File extension without the dot — e.g. "pdf", "csv". */
  ext: string;
  /** Optional audience qualifier — e.g. "internal", "jane-doe". Will be slugified. */
  audience?: string;
  /** Override the date suffix. Defaults to today (YYYY-MM-DD). */
  date?: string;
}

export function exportFilename(opts: ExportFilenameOptions): string {
  const parts = [slugify(opts.eventName), opts.kind];
  if (opts.audience) {
    parts.push(slugify(opts.audience));
  }
  parts.push(opts.date ?? new Date().toISOString().slice(0, 10));
  return `${parts.join("-")}.${opts.ext}`;
}
